import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Ban, Check, ChevronLeft, FileUp, Paperclip, Printer, X } from "lucide-react"
import { useRef, useState } from "react"
import { Link, useParams } from "react-router"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { type ArchivoParaVer, VistaPreviaArchivo } from "@/componentes/ui/VistaPreviaArchivo"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

import { HojaDocumentoTH } from "./HojaDocumentoTH"
import { type DocumentoTH, ESTADOS } from "./tipos"

const fecha = (iso: string) => new Date(iso).toLocaleDateString("es-VE")

/**
 * Un documento de Talento Humano: leerlo, imprimirlo y —quien puede— resolverlo,
 * anularlo o colgarle el escaneado firmado.
 *
 * ⚠ **Es una PÁGINA y no una ficha lateral, por la impresión.** «Imprimir» es la del
 * navegador (texto vectorial, como el manual y la Biblioteca), y una ficha en un
 * portal se escondería con `.no-print` o saldría encima de la lista.
 *
 * ⚠ **Dos puertas, una pantalla.** En `autoservicio` lee de `/mis-documentos` —el
 * servidor sólo devuelve los propios— y no ofrece ninguna acción: el empleado pide e
 * imprime, Talento Humano decide.
 */
export function PaginaDocumentoTH({ modo }: { modo: "th" | "autoservicio" }) {
  const { id } = useParams()
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const enTH = modo === "th"
  const puedeResolver = enTH && tienePermiso(usuario, "documento_th:resolver")
  const puedeEmitir = enTH && tienePermiso(usuario, "documento_th:emitir")
  const base = enTH ? "documentos-th" : "mis-documentos"
  const volver = enTH ? "/talento-humano/documentos" : "/mis-documentos"

  const [rechazando, setRechazando] = useState(false)
  const [anulando, setAnulando] = useState(false)
  const [motivo, setMotivo] = useState("")
  const [verFirmado, setVerFirmado] = useState<ArchivoParaVer | null>(null)
  const archivo = useRef<HTMLInputElement>(null)

  const consulta = useQuery({
    queryKey: [enTH ? "documentos-th" : "mis-documentos", "detalle", id],
    queryFn: () => api.get<DocumentoTH>(`/api/v1/${base}/${id}`),
    enabled: id !== undefined,
  })

  const refrescar = () => {
    clienteQuery.invalidateQueries({ queryKey: ["documentos-th"] })
    clienteQuery.invalidateQueries({ queryKey: ["mis-documentos"] })
  }
  const cerrarMotivo = () => {
    setRechazando(false)
    setAnulando(false)
    setMotivo("")
  }
  const resolver = useMutation({
    mutationFn: (aprobar: boolean) =>
      api.post<DocumentoTH>(`/api/v1/documentos-th/${id}/resolver`, {
        aprobar,
        observacion: motivo.trim() || null,
      }),
    onSuccess: () => {
      refrescar()
      cerrarMotivo()
    },
    meta: { exito: "Solicitud resuelta" },
  })
  const anular = useMutation({
    mutationFn: () => api.post<DocumentoTH>(`/api/v1/documentos-th/${id}/anular`, { motivo }),
    onSuccess: () => {
      refrescar()
      cerrarMotivo()
    },
    meta: { exito: "Documento anulado" },
  })
  const subir = useMutation({
    mutationFn: (f: File) => {
      const datos = new FormData()
      datos.append("archivo", f)
      return api.post<DocumentoTH>(`/api/v1/documentos-th/${id}/firmado`, datos)
    },
    onSuccess: refrescar,
    meta: { exito: "Escaneado firmado guardado" },
  })

  if (consulta.isPending) return <EstadoCarga etiqueta="Abriendo el documento…" />
  if (consulta.isError || !consulta.data) {
    return (
      <EstadoError
        titulo="No se pudo abrir el documento."
        error={consulta.error}
        onReintentar={() => consulta.refetch()}
      />
    )
  }

  const doc = consulta.data
  const estado = ESTADOS[doc.estado]
  const error = resolver.error ?? anular.error ?? subir.error
  const emitible = doc.estado === "EMITIDO" || doc.estado === "APROBADO"

  return (
    <div>
      <div className="no-print mb-4 flex flex-wrap items-center gap-2">
        <Link
          to={volver}
          className="flex items-center gap-1 text-sm"
          style={{ color: "var(--fg-muted)" }}
        >
          <ChevronLeft size={16} aria-hidden="true" />
          {enTH ? "Documentos" : "Mis solicitudes"}
        </Link>
        <Insignia tono={estado.tono}>{estado.etiqueta}</Insignia>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {puedeResolver && doc.estado === "SOLICITADO" && (
            <>
              <Boton variante="fantasma" onClick={() => setRechazando(true)}>
                <X size={15} aria-hidden="true" />
                Rechazar
              </Boton>
              <Boton
                variante="primario"
                disabled={resolver.isPending}
                onClick={() => resolver.mutate(true)}
              >
                <Check size={15} aria-hidden="true" />
                Aprobar
              </Boton>
            </>
          )}
          {doc.tiene_firmado && enTH && (
            <Boton
              variante="fantasma"
              onClick={() =>
                setVerFirmado({
                  ruta: `/api/v1/documentos-th/${doc.id}/firmado`,
                  nombre: `${doc.codigo} firmado`,
                })
              }
            >
              <Paperclip size={15} aria-hidden="true" />
              Ver firmado
            </Boton>
          )}
          {puedeEmitir && emitible && (
            <>
              <input
                ref={archivo}
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                className="sr-only"
                aria-label="Escaneado firmado"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) subir.mutate(f)
                  e.target.value = ""
                }}
              />
              <Boton
                variante="fantasma"
                disabled={subir.isPending}
                onClick={() => archivo.current?.click()}
              >
                <FileUp size={15} aria-hidden="true" />
                {doc.tiene_firmado ? "Reemplazar firmado" : "Subir firmado"}
              </Boton>
            </>
          )}
          {puedeEmitir && doc.estado !== "ANULADO" && doc.estado !== "RECHAZADO" && (
            <Boton variante="fantasma" onClick={() => setAnulando(true)}>
              <Ban size={15} aria-hidden="true" />
              Anular
            </Boton>
          )}
          {doc.estado !== "ANULADO" && doc.estado !== "RECHAZADO" && (
            <Boton variante="secundario" onClick={() => window.print()}>
              <Printer size={15} aria-hidden="true" />
              Imprimir / PDF
            </Boton>
          )}
        </div>
      </div>

      <dl className="no-print mb-4 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
        <div>
          <dt style={{ color: "var(--fg-muted)" }}>Código</dt>
          <dd className="tabular-nums">{doc.codigo}</dd>
        </div>
        <div>
          <dt style={{ color: "var(--fg-muted)" }}>Empleado</dt>
          <dd>{doc.persona ?? "—"}</dd>
        </div>
        <div>
          <dt style={{ color: "var(--fg-muted)" }}>Fecha</dt>
          <dd className="tabular-nums">{fecha(doc.creado_en)}</dd>
        </div>
        {doc.observacion && (
          <div className="sm:col-span-3">
            <dt style={{ color: "var(--fg-muted)" }}>
              {doc.estado === "ANULADO" ? "Motivo de la anulación" : "Observación"}
            </dt>
            <dd>{doc.observacion}</dd>
          </div>
        )}
      </dl>

      {doc.estado === "SOLICITADO" && (
        <p className="no-print mb-4 text-sm" style={{ color: "var(--warning)" }}>
          Pendiente de Talento Humano: no tiene validez hasta que se apruebe.
        </p>
      )}

      {error && (
        <p role="alert" className="no-print mb-4 text-sm" style={{ color: "var(--destructive)" }}>
          {error instanceof ErrorApi ? error.message : "No se pudo completar la acción"}
        </p>
      )}

      <div className="tarjeta p-4 sm:p-8">
        <HojaDocumentoTH
          codigo={doc.codigo}
          titulo={doc.titulo}
          fecha={fecha(doc.creado_en)}
          cuerpo={doc.cuerpo}
        />
      </div>

      <Modal
        abierto={rechazando || anulando}
        titulo={rechazando ? "Rechazar la solicitud" : "Anular el documento"}
        descripcion={
          rechazando
            ? "El empleado verá el motivo."
            : "El documento queda en el registro, marcado como anulado y con su motivo."
        }
        onCerrar={cerrarMotivo}
      >
        <label htmlFor="th-motivo" className="mb-1 block text-xs font-medium uppercase">
          Motivo
        </label>
        <textarea
          id="th-motivo"
          rows={3}
          className="campo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
        />
        <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
          Al menos 10 caracteres.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Boton variante="fantasma" onClick={cerrarMotivo}>
            Cancelar
          </Boton>
          <Boton
            variante="peligro"
            disabled={motivo.trim().length < 10 || resolver.isPending || anular.isPending}
            onClick={() => (rechazando ? resolver.mutate(false) : anular.mutate())}
          >
            {rechazando ? "Rechazar" : "Anular"}
          </Boton>
        </div>
      </Modal>

      <VistaPreviaArchivo archivo={verFirmado} onCerrar={() => setVerFirmado(null)} />
    </div>
  )
}
